/**
 * Documentos de identidad guatemaltecos: DPI (CUI), NIT y teléfonos.
 * Validación, normalización y formato para mostrar.
 */
import { DEPARTMENTS } from './geography.js';
/** Dígitos numéricos de una cadena de dígitos ASCII. */
function digitsOf(value) {
    return value.split('').map(Number);
}
/** Separadores que la gente escribe dentro de un CUI o NIT (espacios y guiones). */
const DOCUMENT_SEPARATORS = /[\s-]/g;
/**
 * Estructura del CUI (13 dígitos): `NNNNNNNN V DD MM`. Cada rango es
 * `[inicio, fin)` para usar directamente con `slice`.
 */
export const CUI_LAYOUT = {
    /** Longitud total del CUI. */
    length: 13,
    /** Correlativo de 8 dígitos. */
    correlative: [0, 8],
    /** Posición del dígito verificador. */
    checkDigitIndex: 8,
    /** Código de departamento (01–22). */
    department: [9, 11],
    /** Código de municipio dentro del departamento. */
    municipality: [11, 13],
};
/** CUI normalizado: exactamente 13 dígitos. */
const CUI_PATTERN = /^\d{13}$/;
/** Módulo del algoritmo de dígito verificador del CUI y del NIT. */
const MOD_11 = 11;
/**
 * Peso del primer dígito del correlativo del CUI: el dígito i-ésimo (base 0)
 * se multiplica por (i + 2).
 */
const CUI_FIRST_WEIGHT = 2;
/** Resultado del módulo 11 que el CUI no admite y el NIT representa como "K". */
const CHECK_VALUE_TEN = 10;
/** Tamaño de los bloques con que se imprime el CUI en el DPI: `2345 67890 0101`. */
const CUI_PRINT_GROUPS = { first: 4, second: 9 };
/** Ancho de los códigos de departamento y municipio dentro del CUI. */
const LOCATION_CODE_WIDTH = 2;
/** Suma ponderada del correlativo del CUI. */
function cuiWeightedSum(correlative) {
    return digitsOf(correlative).reduce((sum, digit, i) => sum + digit * (i + CUI_FIRST_WEIGHT), 0);
}
/**
 * Valida un número de DPI guatemalteco (CUI, Código Único de Identificación).
 *
 * Estructura (13 dígitos): `NNNNNNNN V DD MM`
 *   - NNNNNNNN  correlativo
 *   - V         dígito verificador: Σ(dígitoᵢ × (i + 2)) mod 11 sobre el correlativo
 *   - DD        código de departamento (01–22)
 *   - MM        código de municipio dentro del departamento
 */
export function isValidCui(value) {
    const cui = value.replace(DOCUMENT_SEPARATORS, '');
    if (!CUI_PATTERN.test(cui))
        return false;
    const department = DEPARTMENTS[Number(cui.slice(...CUI_LAYOUT.department)) - 1];
    const municipality = Number(cui.slice(...CUI_LAYOUT.municipality));
    if (!department || municipality < 1 || municipality > department.municipalities)
        return false;
    const correlative = cui.slice(...CUI_LAYOUT.correlative);
    const checkDigit = Number(cui[CUI_LAYOUT.checkDigitIndex]);
    return cuiWeightedSum(correlative) % MOD_11 === checkDigit;
}
/** Quita espacios y guiones: `"2345 67890 0101"` → `"2345678900101"`. */
export function normalizeCui(value) {
    return value.replace(DOCUMENT_SEPARATORS, '');
}
/** Formatea un CUI como se imprime en el DPI: `2345 67890 0101`. */
export function formatCui(value) {
    const cui = normalizeCui(value);
    const { first, second } = CUI_PRINT_GROUPS;
    return cui.length === CUI_LAYOUT.length ? `${cui.slice(0, first)} ${cui.slice(first, second)} ${cui.slice(second)}` : value;
}
/**
 * Construye un CUI válido a partir de un correlativo y códigos de ubicación.
 * Lo usan los seeders y las pruebas para generar identidades realistas.
 */
export function buildCui(correlative, departmentCode, municipalityCode) {
    const [, correlativeLength] = CUI_LAYOUT.correlative;
    const body = String(correlative).padStart(correlativeLength, '0').slice(-correlativeLength);
    const checkDigit = cuiWeightedSum(body) % MOD_11;
    if (checkDigit === CHECK_VALUE_TEN) {
        throw new RangeError('Ese correlativo no admite dígito verificador de un dígito; usa otro');
    }
    const department = String(departmentCode).padStart(LOCATION_CODE_WIDTH, '0');
    const municipality = String(municipalityCode).padStart(LOCATION_CODE_WIDTH, '0');
    return `${body}${String(checkDigit)}${department}${municipality}`;
}
/** NIT genérico de "consumidor final", aceptado como válido. */
export const FINAL_CONSUMER_NIT = 'CF';
/** Carácter verificador del NIT cuando el módulo 11 da 10. */
const NIT_CHECK_K = 'K';
/** NIT normalizado: de 1 a 12 dígitos seguidos del verificador (dígito o K). */
const NIT_PATTERN = /^\d{1,12}[\dK]$/;
/** Longitud mínima para separar cuerpo y verificador al formatear un NIT. */
const NIT_MIN_FORMAT_LENGTH = 2;
/**
 * Valida un NIT guatemalteco (Número de Identificación Tributaria): módulo 11
 * con pesos descendentes; el verificador puede ser `K`. "CF" (consumidor
 * final) se acepta como marcador válido.
 */
export function isValidNit(value) {
    const nit = normalizeNit(value);
    if (nit === FINAL_CONSUMER_NIT)
        return true;
    if (!NIT_PATTERN.test(nit))
        return false;
    const body = nit.slice(0, -1);
    const check = nit.slice(-1);
    const weightedSum = digitsOf(body).reduce((sum, digit, i) => sum + digit * (body.length + 1 - i), 0);
    const expected = (MOD_11 - (weightedSum % MOD_11)) % MOD_11;
    return check === (expected === CHECK_VALUE_TEN ? NIT_CHECK_K : String(expected));
}
/** Pasa a mayúsculas y quita separadores: `"1234567-k"` → `"1234567K"`. */
export function normalizeNit(value) {
    return value.replace(DOCUMENT_SEPARATORS, '').toUpperCase();
}
/** Formatea un NIT con el verificador separado: `1234567K` → `1234567-K`. */
export function formatNit(value) {
    const nit = normalizeNit(value);
    return nit === FINAL_CONSUMER_NIT || nit.length < NIT_MIN_FORMAT_LENGTH ? nit : `${nit.slice(0, -1)}-${nit.slice(-1)}`;
}
/** Código de país de Guatemala para teléfonos. */
export const GUATEMALA_COUNTRY_CODE = '502';
/** Prefijo internacional opcional, solo si le siguen exactamente 8 dígitos. */
const COUNTRY_PREFIX_PATTERN = new RegExp(`^${GUATEMALA_COUNTRY_CODE}(?=\\d{8}$)`);
/**
 * Teléfono guatemalteco: 8 dígitos que empiezan de 2 a 7 (fijos por lo
 * general 2, 6 o 7; móviles 3, 4 o 5).
 */
const GUATEMALAN_PHONE_PATTERN = /^[2-7]\d{7}$/;
/** Dígitos del primer bloque al mostrar un teléfono (`5555 1234`). */
const PHONE_GROUP_LENGTH = 4;
/** Base de los enlaces de WhatsApp. */
const WHATSAPP_BASE_URL = 'https://wa.me/';
/**
 * Normaliza un teléfono guatemalteco a sus 8 dígitos. Acepta el prefijo
 * opcional +502. Devuelve `null` si no es un número válido.
 */
export function normalizeGuatemalanPhone(value) {
    const digits = value.replace(/[^\d]/g, '').replace(COUNTRY_PREFIX_PATTERN, '');
    return GUATEMALAN_PHONE_PATTERN.test(digits) ? digits : null;
}
/** Formatea como `5555 1234`. */
export function formatPhone(value) {
    const phone = normalizeGuatemalanPhone(value);
    return phone ? `${phone.slice(0, PHONE_GROUP_LENGTH)} ${phone.slice(PHONE_GROUP_LENGTH)}` : value;
}
/** Enlace directo de WhatsApp para los recordatorios que envían los asesores desde su teléfono. */
export function whatsappLink(phone, message) {
    const normalized = normalizeGuatemalanPhone(phone);
    return normalized ? `${WHATSAPP_BASE_URL}${GUATEMALA_COUNTRY_CODE}${normalized}?text=${encodeURIComponent(message)}` : null;
}
//# sourceMappingURL=documents.js.map